import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { Zap, RefreshCw, Play, Shield, CheckCircle, AlertTriangle, Pause } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (v) => {
  if (Math.abs(v ?? 0) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v ?? 0) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${(v ?? 0).toFixed(0)}`;
};

const PHASE_STYLE = {
  'ESCALATION': 'bg-amber-100 text-amber-700',
  'PEAK STRESS': 'bg-red-100 text-red-700',
  'RECOVERY': 'bg-emerald-100 text-emerald-700',
};

const TIER_FLOOR = 1_000_000;

export const StressMonthSimulator = ({ isOpen, onClose }) => {
  const [scenario, setScenario] = useState('severe');
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDay, setCurrentDay] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [compare, setCompare] = useState(false);
  const timerRef = useRef(null);

  const runSimulation = useCallback(async (scn) => {
    setLoading(true);
    setError(null);
    setSim(null);
    setCurrentDay(0);
    setPlaying(false);
    try {
      const res = await authFetch(`${API_BASE}/api/lms/simulate/stress-month`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario: scn }),
      });
      if (!res.ok) throw new Error('Simulation failed');
      const data = await res.json();
      setSim(data);
      setPlaying(true);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  // Auto-run on open
  useEffect(() => {
    if (isOpen) runSimulation(scenario);
    if (!isOpen) {
      clearInterval(timerRef.current);
      setSim(null); setCurrentDay(0); setPlaying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Playback engine
  useEffect(() => {
    clearInterval(timerRef.current);
    if (playing && sim) {
      timerRef.current = setInterval(() => {
        setCurrentDay((d) => {
          if (d >= 30) { setPlaying(false); return 30; }
          return d + 1;
        });
      }, 180);
    }
    return () => clearInterval(timerRef.current);
  }, [playing, sim]);

  const day = sim && currentDay > 0 ? sim.days[currentDay - 1] : null;
  const start = sim?.startState;
  const trad = sim?.traditional;
  const finished = currentDay >= 30;
  const chartData = sim ? [
    { day: 0, bufferRemaining: start.buffer, tradCashRemaining: trad?.assumptions?.cashSleeve ?? 0 },
    ...sim.days.slice(0, currentDay),
  ] : [];

  const tierBar = (label, remaining, total, color) => {
    const pct = total > 0 ? Math.max(0, (remaining / total) * 100) : 0;
    return (
      <div className="flex items-center gap-2">
        <div className="w-32 text-xs font-medium text-gray-600">{label}</div>
        <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
          <div className={`h-full ${color} rounded-full transition-all duration-150`} style={{ width: `${pct}%` }} />
        </div>
        <div className="w-16 text-right text-xs text-gray-600">{fmt(remaining)}</div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white" data-testid="stress-month-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Zap className="w-5 h-5 text-red-600" />
            Simulate a Stress Month
          </DialogTitle>
          <DialogDescription>
            30-day live replay against the current liquid buffer. Read-only simulation — the real book is never touched.
          </DialogDescription>
        </DialogHeader>

        {/* Scenario controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { id: 'elevated', label: 'Elevated Redemptions', desc: 'peak 0.6% AUM/day' },
            { id: 'severe', label: 'Severe — GFC-style', desc: 'peak 1.3% AUM/day' },
          ].map((s) => (
            <button
              key={s.id}
              data-testid={`stress-scenario-${s.id}`}
              onClick={() => { setScenario(s.id); runSimulation(s.id); }}
              className={`px-3 py-2 rounded-lg border text-left text-sm transition-all ${
                scenario === s.id ? 'border-red-400 bg-red-50 font-medium' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              {s.label} <span className="text-xs text-gray-400 ml-1">{s.desc}</span>
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="stress-compare-toggle"
              onClick={() => setCompare(!compare)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                compare ? 'border-slate-700 bg-slate-900 text-white font-medium' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className={`w-8 h-4 rounded-full relative transition-colors ${compare ? 'bg-emerald-400' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${compare ? 'left-4' : 'left-0.5'}`} />
              </span>
              Compare to a traditional fund
            </button>
            {sim && !finished && (
              <Button size="sm" variant="outline" onClick={() => setPlaying(!playing)} data-testid="stress-pause-btn">
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => runSimulation(scenario)} disabled={loading} data-testid="stress-replay-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Replay
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {loading && (
          <div className="py-16 text-center text-gray-500">
            <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" /> Computing 30-day stress path…
          </div>
        )}

        {sim && (
          <div className="space-y-4">
            {/* Day header */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-900 text-white">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Stress Replay</p>
                <p className="text-3xl font-bold" data-testid="stress-day-counter">
                  Day {currentDay} <span className="text-lg text-slate-400">/ 30</span>
                </p>
              </div>
              {day && (
                <Badge className={`${PHASE_STYLE[day.phase]} text-sm px-3 py-1`}>{day.phase}</Badge>
              )}
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-slate-400">Liquid Buffer Remaining</p>
                <p className="text-3xl font-bold text-emerald-400">
                  {fmt(day ? day.bufferRemaining : start.buffer)}
                </p>
                <p className="text-xs text-slate-400">
                  of {fmt(start.buffer)} ({(((day ? day.bufferRemaining : start.buffer) / start.buffer) * 100).toFixed(0)}%)
                </p>
              </div>
            </div>

            {/* Daily stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-600 uppercase">Redemptions Today</p>
                <p className="text-lg font-bold text-orange-700">{day ? fmt(day.grossRedemptions) : '—'}</p>
                <p className="text-xs text-gray-500">{day ? `${day.redemptionPctOfAUM.toFixed(2)}% of AUM` : ''}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="text-xs text-emerald-600 uppercase">Deposits Today</p>
                <p className="text-lg font-bold text-emerald-700">{day ? fmt(day.grossDeposits) : '—'}</p>
                <p className="text-xs text-gray-500">{day ? `${day.crossingOffsetPct.toFixed(0)}% crossing offset` : ''}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 uppercase">Net Buffer Drain</p>
                <p className="text-lg font-bold text-red-700">{day ? fmt(day.netDrain) : '—'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Illiquid Sales</p>
                <p className={`text-lg font-bold ${day?.wholesaleNeeded > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                  {day?.wholesaleNeeded > 0 ? fmt(day.wholesaleNeeded) : '$0'}
                </p>
              </div>
            </div>

            {/* Traditional fund live strip */}
            {compare && trad && (
              <div
                data-testid="stress-trad-strip"
                className={`flex items-center justify-between px-4 py-3 rounded-lg border text-sm ${
                  day && day.tradFireSaleToday > 0
                    ? 'bg-red-50 border-red-200'
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <span className="font-medium text-gray-700">
                  Traditional fund <span className="text-xs text-gray-400 font-normal">(5% cash sleeve, no crossing)</span>
                </span>
                {day ? (
                  day.tradFireSaleToday > 0 ? (
                    <span className="font-semibold text-red-700 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" />
                      Cash exhausted — fire-selling {fmt(day.tradFireSaleToday)} of illiquid assets today at a 15% haircut
                    </span>
                  ) : (
                    <span className="text-gray-600">
                      Cash sleeve remaining: <span className="font-semibold">{fmt(day.tradCashRemaining)}</span> of {fmt(trad.assumptions.cashSleeve)}
                    </span>
                  )
                ) : (
                  <span className="text-gray-600">Cash sleeve: <span className="font-semibold">{fmt(trad.assumptions.cashSleeve)}</span></span>
                )}
                {day && day.tradCumLosses > 0 && (
                  <span className="text-red-700 text-xs font-medium">
                    Losses crystallised: {fmt(day.tradCumLosses)}
                  </span>
                )}
              </div>
            )}

            {/* Tier depletion */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <p className="text-xs font-medium text-gray-600 uppercase mb-1">Tier Depletion (sequential, $1M cash floor protected)</p>
              {tierBar('Tier 1 · Cash + T-Bills', day ? day.tier1Remaining : start.tier1, start.tier1, 'bg-emerald-500')}
              {tierBar('Tier 2 · Listed / ETFs', day ? day.tier2Remaining : start.tier2, start.tier2, 'bg-sky-500')}
              {tierBar('Tier 3 · Short Credit', day ? day.tier3Remaining : start.tier3, start.tier3, 'bg-indigo-500')}
            </div>

            {/* Buffer timeline chart */}
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bufGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} domain={[0, 30]} type="number" tickCount={7} />
                  <YAxis tickFormatter={(v) => `$${(v / 1e6).toFixed(0)}M`} tick={{ fontSize: 11 }}
                    domain={[0, Math.ceil(start.buffer / 1e6) * 1e6]} />
                  <Tooltip formatter={(v) => fmt(v)} labelFormatter={(d) => `Day ${d}`} />
                  <Area type="monotone" dataKey="bufferRemaining" name="Trove Helix buffer"
                    stroke="#10b981" strokeWidth={2} fill="url(#bufGrad)" isAnimationActive={false} />
                  {compare && (
                    <Area type="monotone" dataKey="tradCashRemaining" name="Traditional fund cash"
                      stroke="#ef4444" strokeWidth={2} strokeDasharray="5 3"
                      fill="none" isAnimationActive={false} />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Final verdict */}
            {finished && (
              <div
                data-testid="stress-verdict"
                className={`p-5 rounded-xl border ${
                  sim.summary.illiquidSalesRequired
                    ? 'bg-red-50 border-red-200'
                    : 'bg-emerald-50 border-emerald-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {sim.summary.illiquidSalesRequired
                    ? <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5" />
                    : <Shield className="w-6 h-6 text-emerald-600 mt-0.5" />}
                  <div className="flex-1">
                    <p className={`font-semibold text-lg ${sim.summary.illiquidSalesRequired ? 'text-red-800' : 'text-emerald-800'}`}>
                      {sim.summary.illiquidSalesRequired
                        ? `Wholesale fund redemptions required on Day ${sim.summary.wholesaleRequiredOnDay}`
                        : 'Zero illiquid asset sales — buffer absorbed the entire stress month'}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Total Redeemed</p>
                        <p className="font-bold">{fmt(sim.summary.totalRedemptions)} <span className="text-xs font-normal text-gray-500">({sim.summary.totalRedemptionsPctOfAUM}% AUM)</span></p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Crossing Savings</p>
                        <p className="font-bold text-indigo-600">{fmt(sim.summary.transactionCostsSaved)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Buffer Remaining</p>
                        <p className="font-bold">{fmt(sim.summary.bufferRemaining)} <span className="text-xs font-normal text-gray-500">({sim.summary.bufferRemainingPct}%)</span></p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Coverage Left</p>
                        <p className="font-bold">{sim.summary.daysOfCoverageLeft} days <span className="text-xs font-normal text-gray-500">at normal flow</span></p>
                      </div>
                    </div>
                    {!sim.summary.illiquidSalesRequired && (
                      <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Property, PE/VC and Credit positions fully protected — {sim.summary.tier3Touched ? 'Tier 3 partially deployed' : 'Tier 3 never touched'}. Peak day: {fmt(sim.summary.peakDailyRedemption)} redeemed ({sim.summary.peakDailyRedemptionPct}% of AUM).
                      </p>
                    )}
                  </div>
                </div>

                {/* Side-by-side: Trove Helix vs Traditional fund */}
                {compare && trad && (
                  <div className="mt-4 pt-4 border-t border-gray-200" data-testid="stress-comparison-table">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Same month, same flows — two structures</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border border-emerald-200 bg-white p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-4 h-4 text-emerald-600" />
                          <p className="font-semibold text-emerald-800">Trove Helix</p>
                        </div>
                        <dl className="space-y-2 text-sm">
                          <div className="flex justify-between"><dt className="text-gray-500">Forced illiquid sales</dt><dd className="font-bold text-emerald-700">$0</dd></div>
                          <div className="flex justify-between"><dt className="text-gray-500">Investor losses crystallised</dt><dd className="font-bold text-emerald-700">$0</dd></div>
                          <div className="flex justify-between"><dt className="text-gray-500">Crossing savings</dt><dd className="font-bold text-indigo-600">+{fmt(sim.summary.transactionCostsSaved)}</dd></div>
                          <div className="flex justify-between"><dt className="text-gray-500">Redemptions gated?</dt><dd className="font-bold text-emerald-700">Never</dd></div>
                        </dl>
                      </div>
                      <div className="rounded-lg border border-red-200 bg-white p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <p className="font-semibold text-red-800">Traditional fund</p>
                        </div>
                        <dl className="space-y-2 text-sm">
                          <div className="flex justify-between"><dt className="text-gray-500">Forced illiquid sales</dt><dd className="font-bold text-red-700">{fmt(trad.totalFireSalesBookValue)}</dd></div>
                          <div className="flex justify-between"><dt className="text-gray-500">Investor losses (15% haircut)</dt><dd className="font-bold text-red-700">−{fmt(trad.fireSaleLosses)} <span className="text-xs font-normal">({trad.lossPctOfAUM}% AUM)</span></dd></div>
                          <div className="flex justify-between"><dt className="text-gray-500">Crossing savings</dt><dd className="font-bold text-gray-400">$0</dd></div>
                          <div className="flex justify-between"><dt className="text-gray-500">Redemptions gated?</dt><dd className="font-bold text-red-700">{trad.likelyGated ? `Likely by Day ${trad.cashExhaustedOnDay}` : trad.cashExhaustedOnDay ? `At risk (cash out Day ${trad.cashExhaustedOnDay})` : 'No'}</dd></div>
                        </dl>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">{trad.assumptions.note} Fire-sale haircut: {trad.assumptions.fireSaleHaircutPct}%.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
